import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { money, transitCopy } from "@/lib/format";
import type { Address, Parcel, Quote } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner } from "@/components/ui";
import { PROVINCES } from "@/lib/format";
import { AddressSearch } from "@/components/AddressSearch";
import { P } from "@/lib/permissions";
import { useAuth } from "@/state/auth";

const EMPTY: Address = {
  contact_name: "Rate check",
  phone: "0000000000",
  street: "-",
  suburb: "",
  city: "",
  province: "Gauteng",
  postal_code: "",
};

/**
 * A quote without the booking wizard.
 *
 * Sales and support get asked "what would it cost to send this to Sandton"
 * twenty times a day, and walking a four-step booking flow to answer is
 * absurd. Contact details are stubbed because a price does not need them; if
 * the customer says yes, the estimate carries straight into a real booking.
 */
export function RateCalculator() {
  const { can } = useAuth();
  const navigate = useNavigate();

  // Full addresses rather than a suburb and a postal code, so that picking a
  // suggestion carries a coordinate into the estimate. Without one the quote is
  // priced from whatever the server can geocode out of "Sandton", which is a
  // suburb-sized margin of error on a per-kilometre price.
  const [from, setFrom] = useState<Address>({ ...EMPTY, province: "Gauteng" });
  const [to, setTo] = useState<Address>({ ...EMPTY, province: "Gauteng" });
  const [parcel, setParcel] = useState<Parcel>({ length_mm: 300, width_mm: 200, height_mm: 150, weight_g: 2000 });
  const [error, setError] = useState("");

  const quote = useMutation({
    mutationFn: () =>
      api.post<Quote>("/v1/quotes", {
        collection: from,
        delivery: to,
        parcels: [parcel],
      }),
    onMutate: () => setError(""),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not price that route. Try again."),
  });

  const ready = from.city.trim() !== "" && to.city.trim() !== "";

  return (
    <>
      <PageHeader
        title="Rate calculator"
        subtitle="Price a route without starting a booking. Nothing is reserved."
      />

      <Card className="mb-5">
        <h2 className="mb-4 text-lg font-bold tracking-tight">Route</h2>

        {/* Collection and delivery are independent blocks, so on desktop they
            sit beside each other instead of costing a full scroll each. */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <p className="ws-label">Collect from</p>
            <AddressSearch value={from} onChange={setFrom} className="mb-3" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Suburb" value={from.suburb} onChange={(e) => setFrom({ ...from, suburb: e.target.value, latitude: undefined, longitude: undefined })} />
              <Field label="City" value={from.city} onChange={(e) => setFrom({ ...from, city: e.target.value, latitude: undefined, longitude: undefined })} />
              <Select
                label="Province"
                value={from.province}
                onChange={(e) => setFrom({ ...from, province: e.target.value, latitude: undefined, longitude: undefined })}
                options={PROVINCES.map((p) => ({ value: p, label: p }))}
              />
              <Field
                label="Postal code"
                inputMode="numeric"
                maxLength={4}
                value={from.postal_code}
                onChange={(e) => setFrom({ ...from, postal_code: e.target.value, latitude: undefined, longitude: undefined })}
              />
            </div>
          </div>

          <div>
            <p className="ws-label">Deliver to</p>
            <AddressSearch value={to} onChange={setTo} className="mb-3" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Suburb" value={to.suburb} onChange={(e) => setTo({ ...to, suburb: e.target.value, latitude: undefined, longitude: undefined })} />
              <Field label="City" value={to.city} onChange={(e) => setTo({ ...to, city: e.target.value, latitude: undefined, longitude: undefined })} />
              <Select
                label="Province"
                value={to.province}
                onChange={(e) => setTo({ ...to, province: e.target.value, latitude: undefined, longitude: undefined })}
                options={PROVINCES.map((p) => ({ value: p, label: p }))}
              />
              <Field
                label="Postal code"
                inputMode="numeric"
                maxLength={4}
                value={to.postal_code}
                onChange={(e) => setTo({ ...to, postal_code: e.target.value, latitude: undefined, longitude: undefined })}
              />
            </div>
          </div>
        </div>

        <p className="ws-label mt-6">Parcel</p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Field
            label="Length (mm)"
            type="number"
            value={parcel.length_mm}
            onChange={(e) => setParcel({ ...parcel, length_mm: Number(e.target.value) })}
          />
          <Field
            label="Width (mm)"
            type="number"
            value={parcel.width_mm}
            onChange={(e) => setParcel({ ...parcel, width_mm: Number(e.target.value) })}
          />
          <Field
            label="Height (mm)"
            type="number"
            value={parcel.height_mm}
            onChange={(e) => setParcel({ ...parcel, height_mm: Number(e.target.value) })}
          />
          <Field
            label="Weight (g)"
            type="number"
            value={parcel.weight_g}
            onChange={(e) => setParcel({ ...parcel, weight_g: Number(e.target.value) })}
          />
        </div>

        <Button className="mt-5 w-full lg:w-auto" disabled={!ready} loading={quote.isPending} onClick={() => quote.mutate()}>
          Get prices
        </Button>
      </Card>

      <div>
        {error && (
          <div className="mb-4">
            <Alert>{error}</Alert>
          </div>
        )}

        {quote.isPending && (
          <Card className="flex items-center gap-3 text-sm text-ink-500">
            <Spinner /> Working out your options
          </Card>
        )}

        {!quote.data && !quote.isPending && !error && (
          <Card className="py-14 text-center">
            <p className="font-medium">No estimate yet</p>
            <p className="mt-1 text-sm text-ink-500">
              Enter a route above. Prices are what the customer would pay.
            </p>
          </Card>
        )}

        {quote.data && (
          <div className="grid gap-3 lg:grid-cols-2">
            {quote.data.options.map((option) => (
              <Card key={option.id} className="flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{option.service_name}</p>
                    {option.badge && (
                      <Badge tone={option.badge === "cheapest" ? "good" : "progress"}>{option.badge}</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-ink-500">
                    {transitCopy(option.transit_days)}
                    {option.collection_cutoff ? `, collect before ${option.collection_cutoff}` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{money(option.price_cents)}</p>
                </div>
              </Card>
            ))}

            {can(P.book) && (
              <Button variant="secondary" className="mt-1 lg:col-span-2" onClick={() => navigate("/app/book")}>
                Book this shipment
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
